import { observer } from 'mobx-react';
import * as React from 'react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { upperCaseFirst } from 'Util/StringUtils';
import { RefundCardDto } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';
import { TravelCredit } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/TravelCredit';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import If from 'Views/Components/If/If';

export const CardInformation = observer(
	({
		cardsToRefund,
		refundAmount,
		forceFerryCreditRefund,
	}: {
		cardsToRefund?: RefundCardDto[];
		refundAmount: number;
		forceFerryCreditRefund: boolean;
	}) => {
		// Calculate which cards to use and how much to refund to each
		const calculateCardRefunds = (cards: RefundCardDto[], targetAmount: number) => {
			// Sort cards by totalRefundableAmount in descending order (use largest capacity cards first)
			const sortedCards = [...cards]
				?.filter(card => card.totalRefundableAmount !== undefined && card.totalRefundableAmount > 0)
				?.sort((a, b) => (b.totalRefundableAmount || 0) - (a.totalRefundableAmount || 0));

			const result: Array<RefundCardDto & { refundAmount: number }> = [];
			let remainingAmount = targetAmount;

			for (const card of sortedCards) {
				if (remainingAmount <= 0) break;

				const maxRefundForCard = card?.totalRefundableAmount || 0;
				const refundForThisCard = Math.min(remainingAmount, maxRefundForCard);

				if (refundForThisCard > 0) {
					result.push({
						...card,
						refundAmount: refundForThisCard,
					});
					remainingAmount -= refundForThisCard;
				}
			}

			return result;
		};

		const cardRefunds = calculateCardRefunds(cardsToRefund || [], refundAmount);

		if (!refundAmount || refundAmount === 0) {
			return null;
		}

		const totalRefundable = cardRefunds?.reduce((sum, card) => sum + card.refundAmount, 0);
		const isFullCardRefundPossible = totalRefundable >= (refundAmount ?? 0);

		return (
			<div>
				<p className="input-label">Refunds</p>
				<div className="refund-bookings__list">
					<If condition={!forceFerryCreditRefund}>
						{cardRefunds.map((card, index) => (
							<div key={`${card.last4}_${card.cardBrand}`} className="card-refund-item">
								<div className="top-content">
									<span className="card-info">
										<Icon name="credit_card" useMaterialIcons />
										{upperCaseFirst(card.cardBrand ?? '')} ••••{card.last4}
									</span>
									<div className="refund-amount-display">
										<p className="refund-amount">{formatCurrency(card.refundAmount)}</p>
									</div>
								</div>
							</div>
						))}
						{!isFullCardRefundPossible && (
							<div key="manual-refund" className="card-refund-item">
								<div className="top-content">
									<span className="card-info">
										<Icon name="credit_card_gear" useMaterialIcons />
										Manual refund
									</span>
									<div className="refund-amount-display">
										<p className="refund-amount">
											{formatCurrency(Math.abs(totalRefundable - (refundAmount ?? 0)))}
										</p>
									</div>
								</div>
							</div>
						)}
					</If>
					<If condition={forceFerryCreditRefund}>
						<TravelCredit refundAmount={refundAmount} forceFerryCreditRefund={forceFerryCreditRefund} />
					</If>
				</div>
			</div>
		);
	},
);
